import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../services/apiService'

export interface SchedulerStatus {
  isRunning: boolean
  lastRun: string | null
  nextRun: string | null
  rateLimiter: {
    requestsPerSecond: number
    burstSize: number
    queueSize: number
    metrics: {
      totalRequests: number
      successfulRequests: number
      failedRequests: number
      queuedRequests: number
    }
  } | null
  cacheStatus: {
    fast: {
      available: boolean
      timestamp: string | null
      age: number | null
    }
    comprehensive: {
      available: boolean
      timestamp: string | null
      age: number | null
    }
  }
}

export interface SchedulerStatusResponse {
  success: boolean
  data: SchedulerStatus
  message: string
}

export const useSchedulerStatus = (options: { 
  enabled?: boolean
  refetchInterval?: number | ((data: SchedulerStatus | undefined) => number | false)
} = {}) => {
  const { enabled = true, refetchInterval } = options

  return useQuery({
    queryKey: ['scheduler-status'],
    queryFn: async (): Promise<SchedulerStatus> => {
      const response = await apiClient.get<SchedulerStatusResponse>('/team-filters/status')
      const data = response.data

      if (!data.success) {
        throw new Error(data.message || 'Failed to load scheduler status')
      }

      return data.data
    },
    enabled,
    staleTime: 10 * 1000,
    refetchInterval: refetchInterval || false,
    refetchOnWindowFocus: false,
    retry: 2
  })
}

export const useWaitForComprehensiveData = () => {
  const { data: status, isLoading } = useSchedulerStatus({
    enabled: true,
    refetchInterval: (data) => {
      if (data?.cacheStatus?.comprehensive?.available) {
        return false
      }
      return 5000
    }
  })

  return {
    isReady: status?.cacheStatus?.comprehensive?.available || false,
    isLoading,
    status
  }
}
